import {
  getCachedViewSnapshotForSelections,
  subscribeViewProjectionChanges,
} from '@databases/database-state';
import {
  ReduxDatabaseAdapter,
  type PanelStateProvider,
} from '@databases/redux-database-adapter';
import { appStore } from '@databases/redux/store';
import { getService } from '@databases/service-container';
import { getTimelineSelectionEntryIds } from '@databases/timeline-layout/timeline-selection-entry-projection';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import { applyDatabaseView } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { createSelectionsDatabaseSnapshotGetter } from '../bootstrap/create-selections-database-snapshot-getter.ts';
import { type SelectionViewChanges } from '../features/types.ts';
import { resolveSelectionFeatureLayoutType } from '../features/utils/resolve-selection-feature-layout-type.ts';
import { type SelectionLayoutDependencies } from './selection-layout-dependencies.ts';

/**
 * Retains the Table V1 projection, edit policy and Timeline compatibility.
 */
export function createTableV1SelectionDependencies(
  contentId: string,
  layoutType: string | undefined,
): SelectionLayoutDependencies {
  const panelManager = getService('PanelManager');
  const panelStateProvider: PanelStateProvider = {
    getPanelEntryId: () =>
      panelManager.state.panel === 'EntryViewPanel'
        ? (panelManager.getData('EntryViewPanel')?.entryId ?? null)
        : null,
  };
  const getDatabaseSnapshot = createSelectionsDatabaseSnapshotGetter({
    getState: appStore.getState,
    getView: getCachedViewSnapshotForSelections,
    selectDatabase: selectDatabaseByContentIdRequired,
    applyView: applyDatabaseView,
    projectEntryIds:
      layoutType === DatabaseLayoutType.TIMELINE
        ? getTimelineSelectionEntryIds
        : undefined,
  });

  return {
    databaseAdapter: new ReduxDatabaseAdapter({
      getContentId: () => contentId,
      getDatabaseSnapshot,
      getState: appStore.getState,
      panelStateProvider,
    }),
    viewChanges: createSelectionViewChanges(contentId),
    mouseInputAdapter: undefined,
    canEditContent: true,
    featureLayoutTypes:
      layoutType === DatabaseLayoutType.TIMELINE
        ? [layoutType, DatabaseLayoutType.TABLE]
        : [
            layoutType === undefined
              ? undefined
              : resolveSelectionFeatureLayoutType(layoutType),
          ],
    selectionUiMetricsEnabled: layoutType === DatabaseLayoutType.TABLE,
    scrollCellIntoView: undefined,
  };
}

/**
 * Loading or broken databases have no readable view data. Availability gates the
 * adapter's required reads; changes reconcile selections after data is installed.
 */
function createSelectionViewChanges(contentId: string): SelectionViewChanges {
  const getDatabase = () =>
    selectDatabaseByContentIdOptional(appStore.getState(), { contentId });

  return {
    isAvailable: () => {
      const database = getDatabase();

      return database !== undefined && !isBrokenDatabase(database);
    },
    subscribe: (onChange) => {
      let database = getDatabase();
      const unsubscribeDatabase = appStore.subscribe(() => {
        const nextDatabase = getDatabase();

        if (nextDatabase !== database) {
          database = nextDatabase;
          onChange();
        }
      });
      const unsubscribeView = subscribeViewProjectionChanges(onChange);

      return () => {
        unsubscribeView();
        unsubscribeDatabase();
      };
    },
  };
}
